/**
 * SALES INTELLIGENCE — chairman-view of the sales book.
 *
 * Companion to Analytics (which is collections-focused). Renders every
 * signal Sales/Finance need in one dashboard:
 *   1. Hero KPIs — Sales Booked, Sales→Collection ratio, Top 3 sellers, mix
 *   2. Velocity chart — 6m historical (past) + horizon projected
 *   3. Concentration Pareto — per-project share + cumulative line
 *   4. Sales heatmap — project × month, coloured by amount
 *   5. Per-project trends table — totals, avg/month, peak, growth
 *   6. Absorption — how much of the book is already sold per project
 *   7. Sales-to-collection lag — weighted avg months from sale to money
 *
 * All computed server-side (single GET /api/sales/analytics call).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  BadgeDollarSign, TrendingUp, ArrowUpRight, ArrowDownRight,
  Building2, Timer, PieChart, Target,
} from "lucide-react";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, ComposedChart, Line,
  ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis, Cell,
} from "recharts";
import { PageShell } from "@/components/SidebarLayout";
import HorizonSelector from "@/components/HorizonSelector";
import CountUp from "@/components/CountUp";
import { api } from "@/lib/api";
import { formatMoney, monthLabel } from "@/lib/format";
import { useTheme } from "@/lib/ThemeContext";

const CORAL = "#F43F5E";
const INDIGO = "#6366F1";
const EMERALD = "#10B981";
const AMBER = "#F59E0B";
const MUTED = "#94A3B8";

function pctFmt(v) { return `${Math.round((v || 0) * 100)}%`; }

/** Compact tooltip used by every chart on this page. */
function TT({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-card px-3 py-2 text-xs shadow-lg">
      <div className="font-semibold text-ink mb-1">{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 num">
          <span style={{ width: 6, height: 6, background: p.color, borderRadius: 3 }} />
          <span className="text-muted-dark">{p.name}:</span>
          <span className="text-ink font-semibold">
            {typeof p.value === "number" ? formatMoney(p.value) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

export default function SalesIntelligence() {
  const { theme } = useTheme();
  const [horizon, setHorizon] = useState(18);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ok = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/sales/analytics", { params: { horizon } });
        if (ok) setData(data);
      } catch (e) {
        toast.error("Failed to load sales analytics", { description: e.message });
      } finally {
        if (ok) setLoading(false);
      }
    })();
    return () => { ok = false; };
  }, [horizon]);

  const velocitySplit = useMemo(() => {
    if (!data?.velocity) return { historical: [], projected: [] };
    return {
      historical: data.velocity.filter((v) => v.period === "historical"),
      projected: data.velocity.filter((v) => v.period === "projected"),
    };
  }, [data]);

  const pareto = useMemo(() => {
    if (!data?.kpi?.top_3_sellers && !data?.per_project_trends) return [];
    const total = data?.kpi?.total_sales || 0;
    const rows = (data?.per_project_trends || []).map((p) => ({
      name: p.project_name,
      value: p.total,
      share: total ? p.total / total : 0,
    }));
    let cum = 0;
    return rows.map((r) => {
      cum += r.share;
      return { ...r, cum };
    });
  }, [data]);

  const heatmapValues = useMemo(() => {
    if (!data?.sales_by_project_month) return { rows: [], maxVal: 1 };
    const rows = Object.entries(data.sales_by_project_month).map(([name, months]) => ({
      name,
      months,
      total: Object.values(months).reduce((a, b) => a + b, 0),
    })).sort((a, b) => b.total - a.total);
    const maxVal = Math.max(1, ...rows.flatMap((r) => Object.values(r.months)));
    return { rows, maxVal };
  }, [data]);

  const gridStroke = theme === "dark" ? "rgba(255,255,255,0.08)" : "rgba(18,49,89,0.10)";
  const axisTick = theme === "dark" ? "#B7B7C9" : "#6C6F80";

  return (
    <PageShell
      testId="sales-intelligence-page"
      kicker="Chairman-View Sales Intelligence"
      title="Sales Intelligence"
      rangeMonths={data?.months}
      rangeLabel={`${horizon}m`}
      actions={<HorizonSelector value={horizon} onChange={setHorizon} months={data?.months} />}
    >
      <p className="-mt-2 text-muted-dark text-sm max-w-2xl">
        Every signal from the sales book — velocity, concentration, absorption
        and the cash-conversion lag from sale to money-in-bank.
        {loading && <span className="ml-2 animate-pulse">Loading…</span>}
      </p>

      {/* ── 1. HERO KPI STRIP ───────────────────────────────────────────── */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <HeroKPI icon={BadgeDollarSign} label={`Projected Sales · ${horizon}m`} value={data.kpi.total_sales} accent={CORAL} testId="sales-hero-total" />
          <HeroKPI icon={TrendingUp} label="Sales → Projected Collection" value={pctFmt(data.kpi.sales_to_collection_ratio)} sub={`${formatMoney(data.kpi.total_collections)} projected`} accent={EMERALD} testId="sales-hero-ratio" />
          <HeroKPI icon={Target} label="Top-3 Share" value={pctFmt(data.kpi.top_3_sellers.reduce((a, b) => a + b.share, 0))} sub={data.kpi.top_3_sellers.map((t) => t.project_name).join(" · ")} accent={INDIGO} testId="sales-hero-top3" />
          <HeroKPI icon={PieChart} label="New vs Ongoing" value={`${pctFmt(data.kpi.type_mix.new.share)} · ${pctFmt(data.kpi.type_mix.ongoing.share)}`} sub={`Land ${pctFmt(data.kpi.type_mix.land.share)}`} accent={AMBER} testId="sales-hero-mix" />
        </div>
      )}

      {/* ── 2. VELOCITY CHART ───────────────────────────────────────────── */}
      {data && (
        <section className="cf-card p-4 md:p-6">
          <div className="flex items-center justify-between mb-3 md:mb-4">
            <div>
              <h2 className="font-display text-base md:text-lg text-ink font-bold">Sales Velocity</h2>
              <p className="text-[11px] md:text-xs text-muted-dark">6-month history · {horizon}-month projection</p>
            </div>
          </div>
          <div className="h-56 md:h-72 -mx-2 md:mx-0">
            <ResponsiveContainer>
              <ComposedChart data={data.velocity} margin={{ top: 10, right: 8, bottom: 8, left: -8 }}>
                <CartesianGrid stroke={gridStroke} vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={{ fill: axisTick, fontSize: 10 }}
                  tickFormatter={(m) => monthLabel(m).slice(0, 3)}
                  interval="preserveStartEnd"
                  minTickGap={20}
                />
                <YAxis tick={{ fill: axisTick, fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 100)}`} width={32} />
                <Tooltip content={<TT />} labelFormatter={monthLabel} />
                <ReferenceLine x={data.months[0]} stroke={CORAL} strokeDasharray="4 4" label={{ value: "TODAY", fill: CORAL, fontSize: 9, position: "top" }} />
                <Bar dataKey="sales_value" name="Projected Sales" radius={[4, 4, 0, 0]}>
                  {data.velocity.map((v, i) => (
                    <Cell key={i} fill={v.period === "historical" ? MUTED : CORAL} />
                  ))}
                </Bar>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[10px] text-muted-light mt-1">Y-axis in ₹ Cr</p>
        </section>
      )}

      {/* ── 3. CONCENTRATION PARETO ─────────────────────────────────────── */}
      {data && pareto.length > 0 && (
        <section className="cf-card p-4 md:p-6">
          <div className="mb-3 md:mb-4">
            <h2 className="font-display text-base md:text-lg text-ink font-bold">Concentration · Pareto</h2>
            <p className="text-[11px] md:text-xs text-muted-dark">Which projects carry the book. Cumulative share on the right axis.</p>
          </div>
          <div className="h-64 md:h-72 -mx-2 md:mx-0">
            <ResponsiveContainer>
              <ComposedChart data={pareto} margin={{ top: 10, right: 8, bottom: 40, left: -8 }}>
                <CartesianGrid stroke={gridStroke} vertical={false} />
                <XAxis dataKey="name" tick={{ fill: axisTick, fontSize: 9 }} interval={0} angle={-35} textAnchor="end" height={60} />
                <YAxis yAxisId="left" tick={{ fill: axisTick, fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 100)}`} width={32} />
                <YAxis yAxisId="right" orientation="right" tick={{ fill: axisTick, fontSize: 10 }} tickFormatter={(v) => `${Math.round(v * 100)}%`} domain={[0, 1]} width={32} />
                <Tooltip content={<TT />} />
                <Bar yAxisId="left" dataKey="value" name="Projected Sales" fill={CORAL} radius={[4, 4, 0, 0]} />
                <Line yAxisId="right" dataKey="cum" name="Cumulative" stroke={INDIGO} strokeWidth={2.5} dot={{ r: 3, fill: INDIGO }} />
                <ReferenceLine yAxisId="right" y={0.8} stroke={AMBER} strokeDasharray="4 4" label={{ value: "80%", fill: AMBER, fontSize: 9, position: "right" }} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[10px] text-muted-light mt-1">Left axis ₹ Cr · right axis cumulative %</p>
        </section>
      )}

      {/* ── 4. HEATMAP ──────────────────────────────────────────────────── */}
      {data && heatmapValues.rows.length > 0 && (
        <section className="cf-card p-5 md:p-6 overflow-hidden">
          <div className="mb-4">
            <h2 className="font-display text-lg text-ink font-bold">Sales Heatmap</h2>
            <p className="text-xs text-muted-dark">Project × Month · cell darkness = amount booked</p>
          </div>
          <div className="overflow-x-auto -mx-2">
            <table className="min-w-max">
              <thead>
                <tr>
                  <th className="text-left px-2 py-1 text-[10px] uppercase tracking-widest font-semibold text-muted-light sticky left-0 bg-surface">Project</th>
                  {data.months.map((m) => (
                    <th key={m} className="px-2 py-1 text-[10px] uppercase tracking-widest font-semibold text-muted-light text-center">
                      {monthLabel(m).slice(0, 3)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {heatmapValues.rows.map((row) => (
                  <tr key={row.name}>
                    <td className="px-2 py-1 text-xs font-semibold text-ink sticky left-0 bg-surface whitespace-nowrap">{row.name}</td>
                    {data.months.map((m) => {
                      const v = row.months[m] || 0;
                      const intensity = v > 0 ? Math.min(1, v / heatmapValues.maxVal) : 0;
                      const bg = v > 0 ? `rgba(244, 63, 94, ${0.10 + intensity * 0.85})` : "transparent";
                      return (
                        <td key={m} className="p-0.5 text-center">
                          <div
                            className="w-10 h-8 rounded text-[10px] flex items-center justify-center num font-semibold"
                            style={{ background: bg, color: intensity > 0.4 ? "#fff" : "var(--cf-ink)" }}
                            title={`${row.name} · ${monthLabel(m)}: ${formatMoney(v)}`}
                          >
                            {v > 0 ? Math.round(v / 100) : ""}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[10px] text-muted-light mt-2">Values in ₹ Cr · empty cells = no sales that month</p>
        </section>
      )}

      {/* ── 5. PER-PROJECT TRENDS TABLE ─────────────────────────────────── */}
      {data && (
        <section className="cf-card p-5 md:p-6">
          <div className="mb-4">
            <h2 className="font-display text-lg text-ink font-bold">Per-Project Trends</h2>
            <p className="text-xs text-muted-dark">Ranked by total sales. Growth = last-third vs first-third of the horizon.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-muted-light font-semibold border-b border-hairline">
                  <th className="text-left px-3 py-2">Project</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-right px-3 py-2">Total</th>
                  <th className="text-right px-3 py-2">Avg / month</th>
                  <th className="text-right px-3 py-2">Peak</th>
                  <th className="text-right px-3 py-2">Growth</th>
                </tr>
              </thead>
              <tbody>
                {data.per_project_trends.map((p) => (
                  <tr key={p.project_name} className="border-b border-hairline/50">
                    <td className="px-3 py-2 text-sm font-semibold text-ink">{p.project_name}</td>
                    <td className="px-3 py-2 text-xs text-muted-dark capitalize">{p.type}</td>
                    <td className="px-3 py-2 text-sm num text-ink text-right font-bold">{formatMoney(p.total)}</td>
                    <td className="px-3 py-2 text-xs num text-muted-dark text-right">{formatMoney(p.avg_per_month)}</td>
                    <td className="px-3 py-2 text-xs num text-muted-dark text-right">
                      {formatMoney(p.peak_value)}
                      {p.peak_month && <span className="text-[10px] text-muted-light ml-1">{monthLabel(p.peak_month)}</span>}
                    </td>
                    <td className="px-3 py-2 text-xs num text-right">
                      {p.growth_first_to_last_third == null ? (
                        <span className="text-muted-light">—</span>
                      ) : (
                        <span className={`inline-flex items-center gap-1 font-semibold ${p.growth_first_to_last_third >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                          {p.growth_first_to_last_third >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                          {Math.round(Math.abs(p.growth_first_to_last_third) * 100)}%
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── 6. ABSORPTION + 7. SALES-TO-COLLECTION LAG side-by-side ─────── */}
      {data && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <section className="cf-card p-5 md:p-6">
            <div className="mb-4">
              <h2 className="font-display text-base text-ink font-bold flex items-center gap-2">
                <Building2 size={16} className="text-coral" /> Absorption
              </h2>
              <p className="text-xs text-muted-dark">Horizon sales as % of (baseline + horizon). Higher = book selling faster.</p>
            </div>
            <div className="space-y-2">
              {data.absorption.map((a) => (
                <div key={a.project_name} className="flex items-center gap-3">
                  <div className="text-xs font-semibold text-ink w-24 shrink-0 truncate">{a.project_name}</div>
                  <div className="flex-1 h-6 bg-surface-2 rounded relative overflow-hidden">
                    <div
                      className="absolute inset-y-0 left-0"
                      style={{ width: `${(a.absorption_pct * 100).toFixed(1)}%`, background: `linear-gradient(90deg, ${CORAL}, ${AMBER})` }}
                    />
                    <div className="absolute inset-0 flex items-center justify-end px-2 text-xs num font-semibold text-ink">
                      {pctFmt(a.absorption_pct)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="cf-card p-5 md:p-6">
            <div className="mb-4">
              <h2 className="font-display text-base text-ink font-bold flex items-center gap-2">
                <Timer size={16} className="text-coral" /> Sales → Collection Lag
              </h2>
              <p className="text-xs text-muted-dark">Weighted average months from sale to cash-in-hand.</p>
            </div>
            <div className="space-y-2">
              {data.sales_to_collection_lag.map((l) => {
                const pctBar = Math.min(1, l.avg_lag_months / 12); // scale to 12m
                return (
                  <div key={l.project_name} className="flex items-center gap-3">
                    <div className="text-xs font-semibold text-ink w-24 shrink-0 truncate">{l.project_name}</div>
                    <div className="flex-1 h-6 bg-surface-2 rounded relative overflow-hidden">
                      <div
                        className="absolute inset-y-0 left-0"
                        style={{ width: `${(pctBar * 100).toFixed(1)}%`, background: `linear-gradient(90deg, ${EMERALD}, ${INDIGO})` }}
                      />
                      <div className="absolute inset-0 flex items-center justify-end px-2 text-xs num font-semibold text-ink">
                        {l.avg_lag_months}m
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>
      )}
    </PageShell>
  );
}

function HeroKPI({ icon: Icon, label, value, sub, accent, testId }) {
  return (
    <div data-testid={testId} className="glass-card p-4 md:p-5 min-w-0" style={{ borderTop: `3px solid ${accent}` }}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-light font-semibold">
        <Icon size={12} style={{ color: accent }} />
        <span className="truncate">{label}</span>
      </div>
      <div className="num text-lg md:text-2xl text-ink font-bold mt-1.5 truncate">
        {typeof value === "number" ? <CountUp value={value} format={(v) => formatMoney(v)} duration={700} /> : value}
      </div>
      {sub && <div className="text-[11px] text-muted-dark mt-1 truncate">{sub}</div>}
    </div>
  );
}
